import { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import { growthApi } from '../api/phase12Client';
import { Editable } from '../context/EditorContext';

const LAYOUT = { paper_bgcolor: '#fff', plot_bgcolor: '#fff', font: { size: 12, color: '#374151' }, margin: { t: 40, r: 20, b: 60, l: 60 } };

function Node({ n, depth, reload }: { n: any; depth: number; reload: () => void }) {
  const [open, setOpen] = useState(depth < 1);
  const chain = n.linkage_chain || [];
  return (
    <div className={depth ? 'tree-node' : ''}>
      <div className="tree-row">
        <span className="mono">{n.doc_ref}</span>
        <span>
          {n.children.length > 0 && <button className="btn-link" style={{ textDecoration: 'none', marginRight: 6 }} onClick={() => setOpen(!open)}>{open ? '▾' : '▸'}</button>}
          {n.title}
          {n.match_type === 'added' && <span className="flag info">added from doc</span>}{n.match_type === 'model' && <span className="flag info">model addition</span>}
          {n.assumption_flag && <span className="flag">assumption</span>}
        </span>
        <span>{n.gdp_linked ? `${n.contribution_pp.toFixed(3)} pp` : '—'}</span>
        <span className="small">{n.gdp_linked ? <Editable target={{ kind: 'lever', id: n.variable_id, name: n.variable_name }} onSaved={reload}>max {n.max_contribution_pp.toFixed(3)}</Editable> : '—'}</span>
        <span className="small">{n.status}</span>
        <span className="small">
          {chain.slice(0, 2).map((c: any, i: number) => (
            <div key={i}>→ {c.target} {c.elasticity != null && c.linkage_id ? <Editable target={{ kind: 'linkage', id: c.linkage_id, name: `${n.variable_id} → ${c.target}`, value: c.elasticity, unit: c.unit, basis: c.basis, reference: c.reference }} onSaved={reload}>({c.elasticity})</Editable> : c.elasticity != null ? `(${c.elasticity})` : ''} <em>{c.basis}</em></div>
          ))}
          {chain.length > 2 && <div>+{chain.length - 2} more</div>}
        </span>
      </div>
      {open && n.children.map((c: any) => <Node key={c.doc_ref} n={c} depth={depth + 1} reload={reload} />)}
    </div>
  );
}

export function ModelOverviewPage() {
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = () => growthApi.overview().then(setD).catch((e) => setErr(String(e)));
  useEffect(() => { load(); }, []);
  if (err) return <div className="error">{err}</div>;
  if (!d) return <div className="loading">Building the model picture…</div>;
  const t = d.totals;
  const areas = d.areas.filter((a: any) => a.indicator_count > 0);

  return (
    <div>
      <h1 className="page-title">Model Overview</h1>
      <p className="page-subtitle">
        The complete picture: {t.areas} strategic areas → {t.indicators} priority indicators → {t.sub_indicators} sub-indicators → elasticities → GDP.
        Every entry of the PM's Parametric Priorities document is mapped; {t.added_from_document} were missing from the app and have been added.
      </p>
      <div className="kpi-row">
        <div className="kpi"><label>Current GDP growth</label><strong>{d.current_gdp?.toFixed(1) ?? '—'}%</strong><span className="sub">verified</span></div>
        <div className="kpi"><label>PM target</label><strong>{d.pm_target}%</strong></div>
        <div className="kpi"><label>{d.selected_scenario}</label><strong>{d.simulated_gdp.toFixed(2)}%</strong><span className="sub">{d.unconstrained_gdp ? `unconstrained ${d.unconstrained_gdp.toFixed(2)}%` : 'no binding constraint'}</span></div>
        <div className="kpi"><label>GDP-linked levers</label><strong>{t.gdp_linked}</strong><span className="sub">of {t.indicators + t.sub_indicators}</span></div>
        <div className="kpi"><label>Elasticities</label><strong>{t.elasticities}</strong><span className="sub">{t.workbook_elasticities} workbook · {t.literature_elasticities} literature</span></div>
        <div className="kpi"><label>Assumption flags</label><strong>{t.flagged}</strong><span className="sub">levers needing evidence</span></div>
        <div className="kpi"><label>Decisions logged</label><strong>{t.decisions}</strong></div>
      </div>
      {d.binding_constraints?.length > 0 && <div className="card" style={{ borderLeft: '4px solid #DC2626' }}><strong>Binding:</strong> {d.binding_constraints.join('; ')}</div>}

      <div className="grid-2">
        <div className="card">
          <Plot
            data={[
              ...areas.map((a: any) => ({ type: 'bar', name: a.name, x: [a.name], y: [a.contribution_pp], marker: { color: a.color }, offsetgroup: 'current' })),
              { type: 'bar', name: 'Max at full implementation', x: areas.map((a: any) => a.name), y: areas.map((a: any) => a.max_contribution_pp), marker: { color: '#e5e7eb' }, offsetgroup: 'max' },
            ] as any}
            layout={{ ...LAYOUT, title: 'Area contributions to GDP growth (pp): current scenario (coloured) vs maximum (grey)', barmode: 'group', xaxis: { tickangle: -25 }, legend: { orientation: 'h', y: -0.45, font: { size: 10 } } }}
            config={{ displayModeBar: false, responsive: true }} style={{ width: '100%', height: 380 }}
          />
        </div>
        <div className="card">
          <Plot
            data={[{
              type: 'sankey', orientation: 'h',
              node: { label: [...areas.map((a: any) => a.name), 'Simulated GDP growth'], color: [...areas.map((a: any) => a.color), '#111827'], pad: 12, thickness: 16 },
              link: {
                source: areas.map((_: any, i: number) => i), target: areas.map(() => areas.length),
                value: areas.map((a: any) => Math.max(0.001, a.contribution_pp)),
              },
            } as any]}
            layout={{ ...LAYOUT, title: 'Flow of contributions into GDP' }}
            config={{ displayModeBar: false, responsive: true }} style={{ width: '100%', height: 380 }}
          />
        </div>
      </div>

      <div className="card">
        <h3>Strategic areas</h3>
        <table className="data-table">
          <thead><tr><th>Area</th><th>Indicators</th><th>Sub-indicators</th><th>GDP-linked</th><th>Contribution (pp)</th><th>Max (pp)</th><th>Flagged</th><th>Decisions</th></tr></thead>
          <tbody>{areas.map((a: any) => (
            <tr key={a.id}><td><span style={{ display: 'inline-block', width: 10, height: 10, background: a.color, borderRadius: 2, marginRight: 6 }} />{a.name}</td>
              <td>{a.indicator_count}</td><td>{a.sub_indicator_count}</td><td>{a.gdp_linked}</td><td>{a.contribution_pp.toFixed(3)}</td><td>{a.max_contribution_pp.toFixed(3)}</td><td>{a.flagged}</td><td>{a.decisions}</td></tr>
          ))}</tbody>
        </table>
      </div>

      {areas.map((a: any) => (
        <div className="card" key={a.id}>
          <h3 style={{ borderLeft: `4px solid ${a.color}`, paddingLeft: 8 }}>{a.name}</h3>
          <div className="tree-row head"><span>Ref</span><span>Indicator</span><span>Contribution</span><span>Max</span><span>Status</span><span>Linkage → target (elasticity)</span></div>
          {a.indicators.map((n: any) => <Node key={n.doc_ref} n={n} depth={0} reload={load} />)}
        </div>
      ))}
    </div>
  );
}
